import SVG from 'svg.js';
import eventbus from 'tiny-emitter/instance';
import Application from '../application';
import Matrix from '../Matrix';
import { point } from '../Point';
import compasSvgPath from '@/assets/svg/compas.svg'

const getPointFromEvent = (event: PointerEvent, toolSpace: SVG.Nested): { clientX: number; clientY: number } => {
  const { clientX, clientY } = event;

  const { x, y } = point.call(toolSpace, clientX, clientY);

  return {
    clientX: x,
    clientY: y,
  };
};

type Point = {
  x: number;
  y: number;
};

function findArrowPosition(viewCenter: Point, viewSize: Point, pinPosition: Point, buffer: number): Point | null {
  const { x: xc, y: yc } = viewCenter;
  const { x: width, y: height } = viewSize;
  const { x: xp, y: yp } = pinPosition;

  // Calculate the viewbox edges with buffer
  const left = xc - width / 2 + buffer;
  const right = xc + width / 2 - buffer;
  const top = yc - height / 2 + buffer;
  const bottom = yc + height / 2 - buffer;

  if (xp >= left && xp <= right && yp >= top && yp <= bottom) {
    return null;
  }

  // Vector from center to pin
  const vx = xp - xc;
  const vy = yp - yc;

  // Helper to check if an x or y coordinate is within horizontal or vertical bounds
  const withinBounds = (min: number, max: number, value: number): boolean => value >= min && value <= max;

  // Find intersection points and choose the valid one
  let minT = Infinity;
  let intersection: Point | null = null;

  // Check intersection with top and bottom
  if (vy !== 0) {
    const tTop = (top - yc) / vy;
    const xTop = xc + tTop * vx;
    if (tTop > 0 && withinBounds(left, right, xTop)) {
      if (tTop < minT) {
        minT = tTop;
        intersection = { x: xTop, y: top };
      }
    }

    const tBottom = (bottom - yc) / vy;
    const xBottom = xc + tBottom * vx;
    if (tBottom > 0 && withinBounds(left, right, xBottom)) {
      if (tBottom < minT) {
        minT = tBottom;
        intersection = { x: xBottom, y: bottom };
      }
    }
  }

  // Check intersection with left and right
  if (vx !== 0) {
    const tLeft = (left - xc) / vx;
    const yLeft = yc + tLeft * vy;
    if (tLeft > 0 && withinBounds(top, bottom, yLeft)) {
      if (tLeft < minT) {
        minT = tLeft;
        intersection = { x: left, y: yLeft };
      }
    }

    const tRight = (right - xc) / vx;
    const yRight = yc + tRight * vy;
    if (tRight > 0 && withinBounds(top, bottom, yRight)) {
      if (tRight < minT) {
        minT = tRight;
        intersection = { x: right, y: yRight };
      }
    }
  }

  return intersection;
}

export const createCompas = (app: Application) => {
  // the compas is a stylized arrow that always points from the mouse position to the stagepin position
  // it should be displayed at the edge of the current viewbox
  const stagepin = app.seatmap.stagepin;

  if (!stagepin) return;

  let stagepinBox: SVG.RBox;
  let first = true;

  // draw stylized arrow via path
  const compas = app.svg.use('compas', compasSvgPath).size(80, 40);
  const text = app.svg.text('stage').font({ size: 12, weight: 'bold' }).hide();

  const positionArrow = (vb: BoxLike) => {
    console.log('zoomed');
    const { x, y, width, height } = vb;

    const vcx = x + width / 2;
    const vcy = y + height / 2;

    // the first call will be the call after the initial zoom in
    if (first) {
      stagepinBox = stagepin.view.rbox(app.svg);
      first = false;
    }

    const { cx, cy } = stagepinBox;

    const angle = Math.atan2(cy - vcy, cx - vcx);

    // get current zoom level of vb
    const svgWidth = app.svg.width();
    const svgHeight = app.svg.height();

    const zoom = Math.min(svgWidth / width, svgHeight / height);

    const buffer = 100 / zoom;

    const arrowPosition = findArrowPosition({ x: vcx, y: vcy }, { x: width, y: height }, { x: cx, y: cy }, buffer);
    if (arrowPosition) {
      compas.show();
      compas.size(80 / zoom, 40 / zoom).center(arrowPosition.x, arrowPosition.y);
      compas.transform(new Matrix().rotate((angle * 180) / Math.PI, arrowPosition.x, arrowPosition.y) as any);
      text
        .size(12 / zoom)
        .show()
        .center(arrowPosition.x, arrowPosition.y + 30 / zoom);
    } else {
      compas.hide();
      text.hide();
      console.log('No valid arrow position found (pin might be within the viewbox).');
    }
  };

  // positionArrow(app.svg.viewbox());
  eventbus.on('camera:panned', positionArrow);
  eventbus.on('camera:zoomed', positionArrow);

  return () => {
    compas.remove();
    text.remove();
  };
};
